import { z } from 'zod';

import { listQuerySchema } from './common.js';
import {
  moderationSourceSchema,
  moderationStatusSchema,
  MODERATION_CATEGORIES,
  moderationCategorySchema,
  type CreativeApprovalStatus,
  type ModerationCategory,
  type ModerationSource,
  type ModerationStatus,
} from './enums.js';

/**
 * KREATİF ONAYI VE İÇERİK MODERASYONU — Şema borç turu (14 Ağu 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden İKİSİ AYNI DOSYADA
 * ═══════════════════════════════════════════════════════════════════════════
 * Adım 8'in devir notu ikisini birlikte devretmişti ve gerekçesi hâlâ
 * geçerli: *onaysız bir moderasyon kaydı kimseyi bağlamıyor, moderasyonsuz
 * bir onay ise kullanıcıyı sağlayıcının reddedeceği bir şeyi onaylamaya davet
 * ediyor.* Onaycı kartında gördüğü moderasyon satırlarına bakarak karar
 * veriyor; iki dosyaya bölmek, birbirini okumadan anlamı olmayan iki
 * sözleşme üretirdi.
 *
 * `creative.ts`ten AYRI olmasının sebebi farklı: orası kreatifin ÜRETİMİNİ
 * anlatıyor (prompt, format, kredi), buradakiler üretim bittikten SONRA
 * insanın ve sağlayıcının verdiği KARARLAR. `video.js` / `studio.js`
 * ayrımıyla aynı desen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 ÜÇÜNCÜ BİR ONAY YOLU AÇILMADI
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu dosyada bir "karar" ucu YOK ve olmayacak. Kreatif onayı bekleyen iş
 * mevcut kuyruğa (`WorkflowActionLog`, `AWAITING_APPROVAL`) düşüyor, mevcut
 * ekranda listeleniyor (`GET /automations/approvals`) ve mevcut uçtan karara
 * bağlanıyor (`POST /automations/approvals/decide`). Buradaki tek yeni
 * eylem bir TALEP: "bu kreatifi onaya gönder".
 *
 * Alternatif — kreatife özel bir onay kuyruğu — kullanıcının "bu nerede
 * onaylanıyor" sorusunu iki ekran arasında bırakırdı. Aynı yasak
 * `MetricLevel.PRODUCT`ın şemadaki notunda da yazılı.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Onay
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `WorkflowActionLog.actionType` değeri — kreatif onayı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `ACTION_TYPES`E BİLEREK EKLENMEDİ — bu bir unutma değil
 * ═══════════════════════════════════════════════════════════════════════════
 * `ACTION_TYPES` kural editörünün MENÜSÜ: kullanıcının bir otomasyon
 * kuralına koyabileceği aksiyonlar. `creative_approve` oraya girseydi
 * kullanıcı "üretilen her kreatifi otomatik onayla" diye bir kural yazabilir
 * ve insan onayı kapısını tek tıkla ortadan kaldırabilirdi. Onayı
 * otomatikleştirilebilir yapmak, onayı yok etmektir.
 *
 * Emsal `agent-tool.service.ts`te: `create_workflow`, `generate_creative` ve
 * `fix_product_content` da `WorkflowActionLog.actionType`a yazılıyor ve
 * hiçbiri `ACTION_TYPES`te değil. Kolon Prisma'da `String`, enum değil —
 * tam olarak bunun için.
 *
 * "Sözleşme ≠ gerçek" kuralı (`ACTION_TYPES` ↔ `ACTION_TYPES_WITH_EXECUTOR`
 * birlikte güncellenir) bu satırı KAPSAMIYOR: o kural sözleşmede duran ama
 * uygulayıcısı olmayan tipi yasaklıyor. Buradaki durum tersi — uygulayıcısı
 * VAR (`ActionExecutorService`), sözleşmeye bilerek girmiyor. Ayrımı
 * görünür kılmak için liste aşağıda.
 */
export const CREATIVE_APPROVE_ACTION_TYPE = 'creative_approve';

/**
 * `WorkflowActionLog.actionType`ta görülebilen ama kural editöründe
 * SEÇİLEMEYEN tipler.
 *
 * Bu liste bir beyan: "`ACTION_TYPES`te olmamaları kasıtlı". Olmasaydı
 * dosyayı okuyan biri `ActionExecutorService`in switch'inde `ACTION_TYPES`e
 * karşılığı olmayan bir case görüp eksik sanardı.
 *
 * Üçü ajanın önerisi, dördüncüsü kreatif onayı — hepsinin ortak yanı
 * kullanıcının ZAMANLANMIŞ bir kurala koyamayacağı, yalnız tek seferlik
 * doğan işler olması.
 */
export const NON_RULE_ACTION_TYPES = [
  'create_workflow',
  'generate_creative',
  'fix_product_content',
  CREATIVE_APPROVE_ACTION_TYPE,
] as const;
export type NonRuleActionType = (typeof NON_RULE_ACTION_TYPES)[number];

/**
 * Onaya gönderme isteği (`POST /creatives/:id/approval-request`).
 *
 * Gövde bilerek NEREYE yayınlanacağını SORMUYOR. Onay kreatife veriliyor,
 * tek bir yayına değil: bir kez onaylanan kreatif beş ad sete de konabilir.
 * Hedefi de onaya bağlasaydık aynı görsel için beş ayrı onay gerekirdi ve
 * onaycı, kendisini ilgilendirmeyen bir yerleşim kararını da imzalardı.
 */
export const creativeApprovalRequestSchema = z.object({
  /// Onaycıya not — "bu kampanya için, marka rehberi v2'ye göre".
  note: z.string().trim().max(1000).optional(),
});
export type CreativeApprovalRequestInput = z.infer<typeof creativeApprovalRequestSchema>;

/// Talebin yanıtı. `actionLogId` mevcut onay kuyruğundaki satır —
/// arayüz kullanıcıyı oraya götürebilsin.
export interface CreativeApprovalRequestResponse {
  creativeId: string;
  approvalStatus: CreativeApprovalStatus;
  actionLogId: string;
  /// `createdAt + APPROVAL_TTL_DAYS`. Kolonda DEĞİL, türetiliyor —
  /// `approvals.service.ts` ile aynı gerekçe (politika değişince geçmiş
  /// satırlar da yeni politikaya uysun).
  expiresAt: string;
}

/// Kreatif satırının onay yüzü. Kreatif listesi bunu JOIN'siz döndürebiliyor;
/// gerekçe `Creative.approvalStatus` şema notunda.
export interface CreativeApprovalState {
  status: CreativeApprovalStatus;
  approvedById: string | null;
  approvedAt: string | null;
  /// Red gerekçesi. Onaylandığında null'a çekiliyor.
  note: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Moderasyon
// ═══════════════════════════════════════════════════════════════════════════

/// Elle moderasyon kararı (`POST /creatives/:id/moderation`).
export const moderationDecisionSchema = z.object({
  status: moderationStatusSchema,
  category: moderationCategorySchema,
  /// Neden. `CLEARED`da bile yazılabilir: "kontrol edildi, marka izni var".
  detail: z.string().trim().max(2000).optional(),
});
export type ModerationDecisionInput = z.infer<typeof moderationDecisionSchema>;

export const moderationListQuerySchema = listQuerySchema.extend({
  status: moderationStatusSchema.optional(),
  source: moderationSourceSchema.optional(),
  creativeId: z.string().uuid().optional(),
});
export type ModerationListQuery = z.infer<typeof moderationListQuerySchema>;

export interface ModerationVerdictRow {
  id: string;
  creativeId: string;
  /// Kreatifin adı — kuyruk ekranı satır başına ikinci bir sorgu atmasın.
  creativeName: string | null;
  source: ModerationSource;
  status: ModerationStatus;
  provider: string | null;
  category: ModerationCategory;
  detail: string | null;
  decidedById: string | null;
  decidedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Sağlayıcı reddinin sınıflandırılması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 HER `ProviderRejectedError` BİR MODERASYON KARARI DEĞİL.
 *
 * O hata sınıfı üç ayrı şeyi birden taşıyor: içerik politikası reddi,
 * geçersiz prompt ve bozuk kaynak ("fal.ai görsel döndürmedi", "HTTP 422").
 * Üçünü birden moderasyon kaydı saymak defteri yalanla doldururdu —
 * "bu kreatif içerik ihlalinden reddedildi" diyen bir satır, aslında
 * sağlayıcının 500 verdiği bir iş olurdu.
 *
 * Bu yüzden fonksiyon `null` dönebiliyor ve dönmesi NORMAL: sınıflandırılamayan
 * ret için satır AÇILMIYOR. Ret yine `CreativeJob.error`da duruyor, yalnız
 * moderasyon defterine girmiyor.
 *
 * ⚠️ `OTHER` bunun istisnası değil, bir CEVAP: sağlayıcı açıkça içerik
 * politikasından bahsetti ama hangi sınıf olduğunu söylemedi. "Reddedildi,
 * sebebi bilinmiyor" da kaydedilmesi gereken bir bilgi.
 */
export interface ProviderRejectionVerdict {
  category: ModerationCategory;
}

/// Sınıf başına imza kelimeleri. Sıra ÖNEMLİ — ilk eşleşen kazanıyor ve
/// dar sınıflar geniş olandan önce geliyor (`ADULT` metni çoğu zaman
/// "safety" kelimesini de içeriyor).
const CATEGORY_SIGNALS: ReadonlyArray<readonly [ModerationCategory, readonly string[]]> = [
  ['ADULT', ['nsfw', 'nudity', 'sexual', 'explicit content', 'adult content']],
  ['TRADEMARK', ['trademark', 'copyright', 'intellectual property', 'celebrity', 'public figure']],
  ['MISLEADING', ['misleading', 'deceptive', 'false claim', 'medical claim', 'financial advice']],
  ['SAFETY', ['violence', 'violent', 'hate', 'self-harm', 'weapon', 'gore', 'harassment']],
];

/// İçerik politikası reddi olduğunu söyleyen ama sınıf VERMEYEN ifadeler.
/// Bunlar `OTHER`a düşüyor — reddin kendisi kayda değer.
const GENERIC_POLICY_SIGNALS: readonly string[] = [
  'content policy',
  'content_policy',
  'safety filter',
  'safety_checker',
  'moderation',
  'flagged',
  'prohibited',
  'not allowed',
  'blocked by',
  'guidelines',
];

/**
 * Sağlayıcı ret metnini moderasyon sınıfına çevirir; içerik reddi değilse
 * `null`.
 *
 * SAF fonksiyon — ağ, saat, veritabanı yok. Worker'ın hata yolundan ve
 * testten AYNI kod çağrılıyor.
 */
export function classifyProviderRejection(message: string): ProviderRejectionVerdict | null {
  const text = message.toLowerCase();

  for (const [category, signals] of CATEGORY_SIGNALS) {
    if (signals.some((signal) => text.includes(signal))) return { category };
  }

  if (GENERIC_POLICY_SIGNALS.some((signal) => text.includes(signal))) {
    return { category: 'OTHER' };
  }

  return null;
}

/// Arayüzün sınıf filtresi için — `MODERATION_CATEGORIES`in dışa açık hâli.
/// Ayrı bir dizi DEĞİL, aynısı: iki liste ayrışamasın.
export const MODERATION_CATEGORY_OPTIONS = MODERATION_CATEGORIES;

/// Onay durumunun arayüzde okunabilir hâli için anahtarlar. Metin `messages/
/// {tr,en}.json`da; burada yalnız anahtar üretimi var ki iki taraf aynı
/// dizeyi kullansın.
export function creativeApprovalMessageKey(status: CreativeApprovalStatus): string {
  return `creative.approval.status.${status.toLowerCase()}`;
}
